<!-- Read-only, country-aware rendering of a patient profile's fields via a label/value slot. -->

<script lang="ts" setup>
import { CountryTranslations } from '@nzyme/i18n/Country.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { computed } from 'vue';

import { useDateFormat } from '@sigma/client/i18n/useDateFormat.js';
import { CountryContext } from '@sigma/common/i18n/CountryContext.js';
import { GenderTranslation } from '@sigma/common/models/Gender.js';
import type { PatientProfile } from '@sigma/common/patient/PatientProfile.js';

import * as lp from './PatientProfile.loc.js';

export type { PatientProfile };

interface Props {
  profile: PatientProfile;
}

defineProps<Props>();

defineSlots<
  Slots<{
    default: { label: string; value: string };
  }>
>();

const t = useTranslate();
const countryContext = useService(CountryContext);
const country = computed(() => countryContext());
const isUS = computed(() => country.value === 'US');
const formatDate = useDateFormat();
</script>

<template>
  <slot
    v-if="profile.firstName"
    :label="t(lp.firstName)"
    :value="profile.firstName"
  />

  <slot
    v-if="profile.lastName"
    :label="t(lp.lastName)"
    :value="profile.lastName"
  />

  <slot
    v-if="profile.gender"
    :label="t(lp.gender)"
    :value="t(GenderTranslation, profile.gender)"
  />

  <slot
    v-if="profile.data.PL_PESEL"
    :label="t(lp.pesel)"
    :value="profile.data.PL_PESEL"
  />

  <slot
    v-if="profile.data.DOCUMENT_NUMBER"
    :label="t(lp.documentNumber)"
    :value="profile.data.DOCUMENT_NUMBER"
  />

  <slot
    v-if="profile.birthDate"
    :label="t(lp.birthDate)"
    :value="formatDate(profile.birthDate, 'shortDate')"
  />

  <template v-if="isUS">
    <slot
      v-if="profile.address.street"
      :label="t(lp.addressStreet)"
      :value="profile.address.street"
    />

    <slot
      v-if="profile.address.city"
      :label="t(lp.addressCity)"
      :value="profile.address.city"
    />

    <slot
      v-if="profile.address.state"
      :label="t(lp.addressState)"
      :value="profile.address.state"
    />

    <slot
      v-if="profile.address.postCode"
      :label="t(lp.addressPostCode)"
      :value="profile.address.postCode"
    />
  </template>

  <!-- PL/European: Street → Post Code, City → Country -->
  <template v-else>
    <slot
      v-if="profile.address.street"
      :label="t(lp.addressStreet)"
      :value="profile.address.street"
    />

    <slot
      v-if="profile.address.postCode"
      :label="t(lp.addressPostCode)"
      :value="profile.address.postCode"
    />

    <slot
      v-if="profile.address.city"
      :label="t(lp.addressCity)"
      :value="profile.address.city"
    />

    <slot
      v-if="profile.address.country"
      :label="t(lp.addressCountry)"
      :value="t(CountryTranslations[profile.address.country])"
    />
  </template>
</template>
